import type { ReactNode } from 'react';
import { ToolbarActions } from './ToolbarActions.js';

export interface PageHeaderProps {
  /** Page title rendered as the `<h1>`. */
  title: ReactNode;
  /**
   * Optional subtitle line under the title. When omitted (e.g. role not yet
   * known) no subtitle node is rendered at all; when provided it is hidden on
   * narrow viewports (kept in the DOM, `display:none`) to match the existing
   * list-header behaviour.
   */
  subtitle?: ReactNode;
  /**
   * Optional compact metadata line rendered beside the title. Unlike
   * `subtitle` it survives the narrow viewport: the facts a phone reader opens
   * the page for cannot be the ones the layout drops.
   */
  meta?: ReactNode;
  /** Narrow-viewport flag (caller owns `useNarrow`), drives the responsive layout. */
  narrow: boolean;
  /** Action controls (filter toggles, create buttons) rendered inside `ToolbarActions`. */
  actions?: ReactNode;
}

/**
 * @aiPurpose Shared list/detail page header: title + optional subtitle on the
 *   left, action controls wrapped in `ToolbarActions` on the right, collapsing
 *   to a single stretched column on narrow viewports. Pure presentation — all
 *   copy and gating flow in from the caller. Styles are carried over verbatim
 *   from the request list headers to guarantee no visual regression.
 */
export function PageHeader({ title, subtitle, meta, narrow, actions }: PageHeaderProps) {
  return (
    <div
      style={{
        padding: '18px 24px 12px',
        display: 'flex',
        flexDirection: narrow ? 'column' : 'row',
        alignItems: narrow ? 'stretch' : 'flex-end',
        gap: narrow ? 12 : 16,
        borderBottom: '1px solid var(--border-1)',
        flex: 'none',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 12,
            minWidth: 0,
          }}
        >
          <h1
            style={{
              margin: '4px 0 0',
              fontSize: 22,
              fontWeight: 600,
              color: 'var(--fg-1)',
              letterSpacing: '-.005em',
            }}
          >
            {title}
          </h1>
          {meta}
        </div>
        {subtitle != null && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--fg-3)',
              marginTop: 2,
              display: narrow ? 'none' : 'block',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {!narrow && <div style={{ flex: 1 }} />}
      {actions != null && (
        <ToolbarActions style={{ gap: narrow ? 8 : 16 }}>{actions}</ToolbarActions>
      )}
    </div>
  );
}
